import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import FolderExternalChecksItemComponent from './ItemComponent';
import { getPagingLimits, filterByValue } from './common/functions';
import debounce from 'lodash.debounce';
import FolderExternalChecksToolbarComponent from './Toolbar';
import { IconButton } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { Grid, Link } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { sortList } from 'utils/functions';
import DoneAll from '@mui/icons-material/DoneAll';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { emptyString } from 'constants/emptyConst';

const FolderExternalChecksListContainer = ({ checks, folderId, folderSiteId }) => {
  const defaultPager = useRef({ page: 1, pageSize: 5 });
  const rowsPerPageOptions = useRef([5, 10, 20]);
  const DEBOUNCE_DELAY = useRef(500);

  const [t] = useTranslation();

  const [filter, setFilter] = useState(emptyString);
  const [data, setData] = useState(checks);
  const [dataOnPage, setDataOnPage] = useState(checks);
  const [pager, setPager] = useState(defaultPager.current);
  const [totalCount, setTotalCount] = useState(0);

  const verificationSorterOptions = useMemo(
    () => [
      { id: 1, name: t('DocumentEdit.Verification.Entity'), orderByProperty: 'entityName', descending: false },
      { id: 2, name: t('DocumentEdit.Verification.Type'), orderByProperty: 'verificationName', descending: false }
    ],
    [t]
  );

  const defaultVerificationSorterOption = {
    id: 1,
    name: t('DocumentEdit.Verification.Entity'),
    orderByProperty: 'entityName',
    descending: false
  };

  const [currentVerificationOrder, setCurrentVerificationOrder] = useState(defaultVerificationSorterOption);

  const onUpdateData = useCallback(data => setData(data), []);

  useEffect(() => {
    setData(sortList(checks, defaultVerificationSorterOption.orderByProperty, defaultVerificationSorterOption.descending));
  }, [checks, defaultVerificationSorterOption.orderByProperty, defaultVerificationSorterOption.descending]);

  //#region pagination and filtering
  const pushFiltersAndPaging = useCallback(
    (filter, pager) => {
      setFilter(filter);
      setPager(pager);

      const { indexOfFirstItem, indexOfLastItem } = getPagingLimits(pager);

      if (filter !== emptyString) {
        const filteredVerifications = filterByValue(data, filter);
        const currentVerifications = filteredVerifications.slice(indexOfFirstItem, indexOfLastItem);

        setDataOnPage(currentVerifications);
        setTotalCount(filteredVerifications.length);
      } else {
        setDataOnPage(data.slice(indexOfFirstItem, indexOfLastItem));
        setTotalCount(data.length);
      }
    },
    [data]
  );

  useEffect(() => {
    if (data) {
      const pagingLimits = getPagingLimits(pager);

      setDataOnPage(data.slice(pagingLimits.indexOfFirstItem, pagingLimits.indexOfLastItem));
      setTotalCount(data.length);
    }
  }, [pager, data]);

  useEffect(() => {
    pushFiltersAndPaging(filter, pager);
  }, [pager, filter, pushFiltersAndPaging]);

  const onFilterValueChangedDebounce = () => setPager(defaultPager.current);

  const filterChangedDebounced = useRef(debounce(onFilterValueChangedDebounce, DEBOUNCE_DELAY.current));

  const handleChangePage = useCallback(
    page => {
      const newPager = Object.assign({}, pager, { page: page + 1, pageSize: pager.pageSize });
      pushFiltersAndPaging(filter, newPager);
    },
    [filter, pager, pushFiltersAndPaging]
  );

  const handleChangeRowsPerPage = useCallback(
    pageSize => {
      const newPager = Object.assign({}, pager, { page: 1, pageSize: pageSize });
      pushFiltersAndPaging(filter, newPager);
    },
    [filter, pager, pushFiltersAndPaging]
  );

  const handleVerificationSorterChanged = useCallback(
    id => {
      const selectedVerificationOrder = { ...verificationSorterOptions.find(x => x.id === id) };
      setData(previousState => sortList(previousState, selectedVerificationOrder.orderByProperty, selectedVerificationOrder.descending));
      setCurrentVerificationOrder(selectedVerificationOrder);
    },
    [verificationSorterOptions]
  );

  const onVerificationsClick = useCallback(() => {
    window.open(`/folders/${folderId}/${folderSiteId}/verifications`, '_blank');
  }, [folderId, folderSiteId]);

  const handleRefresh = useCallback(() => setPager(defaultPager.current), []);

  const handleFilterValueChanged = useCallback((value, debounced) => {
    setFilter(value);

    if (debounced) filterChangedDebounced.current(value);
    else setPager(defaultPager.current);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilter(emptyString);
    setPager(defaultPager.current);
  }, []);

  //#endregion pagination and filtering

  return (
    <CardNoResultsWrapper content={data}>
      {!(data && data.length) && (
        <Grid item>
          <IconButton onClick={onVerificationsClick} color='primary' variant='text' id={'folder_decision_externalChecksCard_verifications'}>
            <DoneAll />
          </IconButton>
          <Link onClick={onVerificationsClick}>{t('Placeholders.Verifications')}</Link>
        </Grid>
      )}
      <FolderExternalChecksToolbarComponent
        fullTextFilter={filter}
        onFilterValueChanged={handleFilterValueChanged}
        onResetFilters={handleResetFilters}
        onVerificationsClick={onVerificationsClick}
        onVerificationSorterChanged={handleVerificationSorterChanged}
        verificationSorterOptions={verificationSorterOptions}
        selectedVerificationOrder={currentVerificationOrder}
      />
      <Grid container flexDirection='column' gap={1} my={1}>
        {dataOnPage.map((elem, index) => (
          <FolderExternalChecksItemComponent
            key={elem.documentVerificationId}
            check={elem}
            list={data}
            onUpdateData={onUpdateData}
            index={index}
          />
        ))}
      </Grid>
      <PaginationWrapper
        count={totalCount}
        pageSize={pager.pageSize}
        page={pager.page}
        rowsPerPageOptions={rowsPerPageOptions.current}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onPageChange={handleChangePage}
        onRefresh={handleRefresh}
      />
    </CardNoResultsWrapper>
  );
};

FolderExternalChecksListContainer.propTypes = {
  checks: PropTypes.array,
  folderId: PropTypes.number.isRequired,
  folderSiteId: PropTypes.number.isRequired
};

export default FolderExternalChecksListContainer;
